import React from 'react';
import { styled } from '@mui/system';
import { Link } from 'react-router-dom';
import { Zap, Handshake, Target, AlertTriangle } from 'lucide-react';
import { getLocalizedPath } from 'helpers/getLocale';
import { getSkillSlug } from 'utils/seo';
import { Card } from './ui';
import { motion, popIn, glow, fadeUp, REDUCED, useCountUp } from './motion';
import { L, t } from '../strings';

// One card that stretches to the height of the step column (the parent grid
// uses align-items: stretch), so the two columns always end on the same line.
// Its content sticks to the top while a long step scrolls.
const Panel = styled(Card)(() => ({
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  marginBottom: 0
}));

const Inner = styled('div')(() => ({
  position: 'sticky',
  top: 20
}));

const Row = styled('div')(({ delay }) => ({
  marginBottom: 16,
  ...motion(`${fadeUp} 360ms ease-out ${delay}ms both`)
}));

const LabelRow = styled('div')(({ theme, tone }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  color: tone === 'low' ? '#b21625' : theme.palette.textSecondary.main,
  marginBottom: 6,
  transition: 'color 300ms ease'
}));

const Label = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  '& svg': { color: theme.palette.grey[200], flexShrink: 0 }
}));

const Value = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 500,
  color: theme.palette.textPrimary.main
}));

const Track = styled('div')(({ theme }) => ({
  position: 'relative',
  height: 8,
  borderRadius: 100,
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden'
}));

const barColor = (v) =>
  v >= 60
    ? 'linear-gradient(90deg, #a6e33a, #79c912)'
    : v >= 35
      ? 'linear-gradient(90deg, #f2c94c, #d5921b)'
      : 'linear-gradient(90deg, #e5646f, #b21625)';

const Fill = styled('div')(() => ({
  height: '100%',
  borderRadius: 100,
  transition: 'width 700ms cubic-bezier(.2,.8,.2,1)',
  '&.changed': motion(`${glow} 900ms ease-out`)
}));

const Delta = styled('span')(({ positive }) => ({
  display: 'inline-block',
  padding: '0 6px',
  borderRadius: 100,
  fontSize: 12,
  lineHeight: '18px',
  color: positive ? '#4f9a04' : '#b21625',
  backgroundColor: positive ? 'rgba(121, 201, 18, 0.16)' : 'rgba(211, 47, 47, 0.10)',
  fontWeight: 500,
  marginLeft: 6,
  ...motion(`${popIn} 360ms ease-out both`)
}));

const Warn = styled(AlertTriangle)(() => ({
  marginLeft: 6,
  ...motion(`${popIn} 300ms ease-out both`),
  [REDUCED]: { animation: 'none' }
}));

const KPI_ICON = { energy: Zap, trust: Handshake };

function KpiRow({ label, kpiKey, value, delta, delay }) {
  const shown = useCountUp(value);
  const Icon = KPI_ICON[kpiKey] || Target;
  const low = value < 35;
  return (
    <Row delay={delay}>
      <LabelRow tone={low ? 'low' : undefined}>
        <Label>
          <Icon size={15} />
          {label}
        </Label>
        <Value>
          {Math.round(shown)}
          {delta ? (
            <Delta key={`${kpiKey}-${value}`} positive={delta > 0}>
              {delta > 0 ? '+' : ''}
              {delta}
            </Delta>
          ) : low ? (
            <Warn size={14} color="#b21625" />
          ) : null}
        </Value>
      </LabelRow>
      <Track>
        <Fill
          key={delta ? `${kpiKey}-${value}` : kpiKey}
          className={delta ? 'changed' : ''}
          style={{ width: `${shown}%`, background: barColor(shown) }}
        />
      </Track>
    </Row>
  );
}

const Title = styled('div')(() => ({
  fontFamily: 'Unbounded',
  fontSize: 14,
  fontWeight: 600,
  marginBottom: 18
}));

const Divider = styled('hr')(({ theme }) => ({
  border: 0,
  borderTop: `1px solid ${theme.palette.grey[100]}`,
  margin: '10px 0 24px'
}));

const Chips = styled('div')(() => ({ display: 'flex', flexWrap: 'wrap', gap: 8 }));

// Competency chips link to the matching entry in the skills directory when
// the pack maps one (`competency.skill`); unmapped ones stay plain.
const chipStyle = (theme) => ({
  display: 'inline-block',
  padding: '5px 12px',
  borderRadius: 100,
  border: `1px solid ${theme.palette.grey[100]}`,
  fontSize: 12,
  fontFamily: 'Rubik-Regular',
  color: theme.palette.textPrimary.main,
  textDecoration: 'none'
});
const CompChip = styled('span')(({ theme }) => chipStyle(theme));
const CompLink = styled(Link)(({ theme }) => ({
  ...chipStyle(theme),
  borderColor: theme.palette.primary.salad,
  '&:hover': { backgroundColor: theme.palette.primary.salad }
}));

export const skillUrl = (skill) => getLocalizedPath(`/skills/${getSkillSlug(skill)}/${skill._id}`);

export const CompetencyChip = ({ competency }) =>
  competency.skill ? (
    <CompLink to={skillUrl(competency.skill)} title={competency.skill.name}>
      {L(competency.label)}
    </CompLink>
  ) : (
    <CompChip>{L(competency.label)}</CompChip>
  );

const Goal = styled('div')(({ theme }) => ({
  marginTop: 24,
  paddingTop: 24,
  borderTop: `1px solid ${theme.palette.grey[100]}`,
  fontFamily: 'Rubik-Regular',
  fontSize: 14,
  lineHeight: '21px',
  color: theme.palette.textSecondary.main,
  '& b': {
    display: 'block',
    color: theme.palette.textPrimary.main,
    fontWeight: 500,
    marginBottom: 4
  }
}));

export default function KpiPanel({ pack, kpis, lastFx }) {
  const rows = [
    ...pack.kpis.map((k) => ({ key: k.key, label: L(k.label) })),
    { key: 'trust', label: t('trust') },
    { key: 'energy', label: t('energy') }
  ];
  return (
    <Panel>
      <Inner>
        <Title>{t('kpis')}</Title>
        {rows.map((r, i) => (
          <KpiRow
            key={r.key}
            kpiKey={r.key}
            label={r.label}
            value={kpis[r.key]}
            delta={lastFx ? lastFx[r.key] : 0}
            delay={i * 50}
          />
        ))}
        <Divider />
        <Title>{t('competencies')}</Title>
        <Chips>
          {pack.competencies.map((c) => (
            <CompetencyChip key={c.key} competency={c} />
          ))}
        </Chips>
        <Goal>
          <b>{t('yourGoal')}</b>
          {L(pack.goal)}
        </Goal>
      </Inner>
    </Panel>
  );
}
